import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink, Loader2 } from "lucide-react";
import { CONTACTS } from "@/data/contacts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ rating }) {
  const full = Math.round(rating || 0);
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${rating} su 5`}>
      {[1,2,3,4,5].map((i) => (
        <Star key={i} size={12} className={i <= full ? "text-gold fill-gold" : "text-white/20"} />
      ))}
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/reviews`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ configured: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="mt-10 rounded-2xl bg-ash/60 border border-white/10 p-6 flex items-center gap-3 text-white/50 text-sm" data-testid="google-reviews-loading">
        <Loader2 className="animate-spin" size={14} /> Carico le recensioni...
      </div>
    );
  }

  // Fallback: not configured yet (no API key) — show CTA to Google reviews
  if (!data?.configured || !data.reviews?.length) {
    return (
      <div className="mt-10 rounded-2xl bg-ash/60 border border-white/10 p-6" data-testid="google-reviews-widget">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full grid place-items-center bg-white shrink-0">
            <GoogleG />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-white">Recensioni Google</div>
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((i) => <Star key={i} size={12} className="text-gold fill-gold" />)}
              </div>
            </div>
            <p className="mt-2 text-sm text-white/60 leading-relaxed">
              Scopri cosa dicono i nostri clienti su Google.
            </p>
            <a
              href={CONTACTS.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="mt-4 inline-flex items-center gap-2 border border-white/15 hover:border-gold text-white/85 hover:text-gold text-sm px-4 py-2 rounded-full transition-colors"
            >
              Leggi su Google <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Live reviews from Google Places API
  return (
    <div className="mt-10 space-y-4" data-testid="google-reviews-live">
      <div className="rounded-2xl bg-ash/60 border border-white/10 p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-full grid place-items-center bg-white shrink-0">
          <GoogleG />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-white">{data.name || "Drago Ramen"}</div>
            {data.rating != null && (
              <>
                <Stars rating={data.rating} />
                <span className="text-sm text-white/70">{data.rating.toFixed(1)}</span>
              </>
            )}
            {data.count != null && (
              <span className="text-xs text-white/45">· {data.count} recensioni</span>
            )}
          </div>
          <div className="text-xs text-white/40 mt-1">Recensioni aggiornate da Google</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.reviews.slice(0, 4).map((r, i) => (
          <div key={r.published || `${r.author_name}-${i}`} className="rounded-2xl bg-ash/50 border border-white/10 p-5" data-testid={`review-${i}`}>
            <div className="flex items-center gap-3">
              {r.author_photo ? (
                <img src={r.author_photo} alt={r.author_name} className="w-8 h-8 rounded-full" loading="lazy" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gold/20 text-gold grid place-items-center text-xs font-medium">
                  {(r.author_name || "?")[0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{r.author_name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Stars rating={r.rating} />
                  <span className="text-[10px] text-white/40">{r.relative_time || ""}</span>
                </div>
              </div>
            </div>
            {r.text && <p className="mt-3 text-sm text-white/70 leading-relaxed line-clamp-4">{r.text}</p>}
            {r.google_url && (
              <a href={r.google_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-gold">
                Vedi su Google <ExternalLink size={9} />
              </a>
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/40">Recensioni fornite da Google · <a href={CONTACTS.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gold">Vedi tutte</a></div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.72.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
